import { authClient } from './auth-client.js';
import {
  ApiError,
  cancelPendingEmailChange,
  getPendingEmailChange,
  reauthenticate,
  requestEmailChange,
} from './api.js';
import { staleSessionMessage } from './refusals.js';

/**
 * Moving the account to another email address, and taking that back.
 *
 * ── WHY THIS IS A FLOW AND NOT A FIELD ───────────────────────────────
 *
 * Profile showed the address read-only for months with a note saying a change
 * was coming, and the note was right about the reason: `/change-email` sends a
 * link and changes nothing. Putting it in the profile form would have implied
 * Save changes it, when Save only starts it.
 *
 * ── AND WHY IT LIVES ON THE SECURITY SCREEN ──────────────────────────
 *
 * Because the address IS a credential here, which is the conclusion
 * RidgeWill/auth#324 arrived at: every provider sign-in resolves through the
 * email address, so whoever controls the mailbox controls the account. Moving
 * it is gated like one — a fresh session, the current password, and for some
 * accounts approval from the address being left — and every fact those gates
 * are decided on is already on the Security screen.
 *
 * It is also where the answer lands. A change started on Profile would show its
 * confirmation, and the cancel control for it, on a different page.
 *
 * ── THE THREE STATES ARE NOT TWO ─────────────────────────────────────
 *
 * `undefined` while the lookup is outstanding, `unknown` when it failed, and a
 * definite `none` or `pending` otherwise. The section renders all four
 * differently, and it has to: drawing "no change is pending" from a failed
 * request is a claim about somebody's account made from no evidence, and it is
 * the reassuring direction, which is the worse one to be wrong in.
 */

export type PendingChange = {
  /** The address the account is being moved TO. Never the current one. */
  newEmail: string;
  /** When it was asked for. Null if the service sent no timestamp. */
  requestedAt: Date | null;
  /**
   * Which address the change is waiting on: `true` for the CURRENT one, `false`
   * for the new one.
   *
   * The SERVER's answer (RidgeWill/auth#334), not this app's. The two states
   * read as opposites to the person in them — "approve it from this inbox"
   * against "open the link we sent to the other one" — and the portal can only
   * get it right from the record the change was written into.
   */
  awaitingApproval: boolean;
};

export type PendingChangeState =
  | { status: 'none' }
  | ({ status: 'pending' } & PendingChange)
  | { status: 'unknown' };

/**
 * Read the pending change, if there is one.
 *
 * Never throws and never reports `none` on a failure — see the three-states
 * note above. `awaitingApproval` is optional on the wire and defaults to
 * `false` here, because this app deploys independently of the service and can
 * be running against a build from before RidgeWill/auth#334. False is the
 * right default for an absent field: it produces "open the link we sent you",
 * which is true of every pending change, rather than sending somebody to look
 * for an approval email that may not exist.
 */
export async function loadPendingChange(): Promise<PendingChangeState> {
  try {
    const out = await getPendingEmailChange();
    if (!out.pendingEmail) return { status: 'none' };
    return {
      status: 'pending',
      newEmail: out.pendingEmail,
      requestedAt: out.requestedAt ? new Date(out.requestedAt) : null,
      awaitingApproval: out.awaitingApproval === true,
    };
  } catch {
    return { status: 'unknown' };
  }
}

/**
 * Cancel the pending change. Returns null on success, or a message to show.
 *
 * The endpoint answers 200 even when there was nothing to cancel, so the only
 * failures reaching here are transport ones. Worth a sentence anyway: the
 * button that calls this is the safest act on the screen and somebody pressing
 * it is already worried.
 */
export async function cancelChange(): Promise<string | null> {
  try {
    await cancelPendingEmailChange();
    return null;
  } catch (e) {
    if (e instanceof ApiError) return e.message;
    return 'That could not be cancelled just now. Nothing has changed, so try again in a moment.';
  }
}

/**
 * Ask to move the account to `newEmail`. Returns null on success.
 *
 * Never throws, for the reason `unlinkProvider` does not: the caller is a
 * submit handler, and an unhandled rejection there leaves a form spinning.
 *
 * ── THE REFUSALS, AND WHY EACH IS TRANSLATED OR NOT ──────────────────
 *
 * `SESSION_NOT_FRESH` shares its sentence with unlinking a provider, because
 * the auth service applies the same freshness bar to both on purpose.
 *
 * `PASSWORD_REQUIRED` and `INVALID_PASSWORD` are passed through. The service
 * wrote both for a person to read, and the second is deliberately the same
 * message `/change-password` gives — a distinct one would tell an attacker
 * which of the two checks they failed.
 *
 * A 400 with NO code is the same-address refusal, which better-auth raises
 * before any of our hooks and words as "Email is the same". The form does not
 * let it happen, so this is a backstop rather than a path anybody should meet;
 * it is reworded because the upstream string reads like a log line.
 *
 * A 404 means the deployment does not serve `/change-email` at all. It is on
 * `ACCOUNT_SERVES` and the contract test pins that, so this is the failure of
 * an allow-list rather than of the request, and saying "try again" about it
 * would be a lie.
 */
export async function requestChange(
  newEmail: string,
  currentPassword?: string,
): Promise<string | null> {
  try {
    await requestEmailChange(newEmail, currentPassword);
    return null;
  } catch (e) {
    if (e instanceof ApiError) {
      if (e.code === 'SESSION_NOT_FRESH' || e.status === 403) {
        return staleSessionMessage('changing your email address');
      }
      if (e.status === 404) {
        return 'Changing your email address is not available on this site just now.';
      }
      if (!e.code && e.status === 400 && /email is the same/i.test(e.message)) {
        return 'That is already your email address.';
      }
      return e.message;
    }
    return 'That could not be sent just now. Your email address has not changed.';
  }
}

/**
 * How many passkeys this account has, for the gate hint below. -1 when the
 * lookup failed, which the hint reads as "do not guess".
 *
 * ── THE PASSKEYS SECTION ALSO ASKS THIS, AND THAT IS DELIBERATE ──────
 *
 * Two calls to one route on one page load, which this codebase argues against
 * elsewhere and for a good reason: two sections DISPLAYING one fact can
 * disagree, and a screen arguing with itself is worse than a slow one.
 *
 * Nothing is displayed from this. It decides whether the change form warns
 * about an extra step, while the Passkeys section owns every claim the page
 * makes about passkeys themselves, so there is no second answer to contradict
 * the first. The alternative was threading a count up out of a sibling section
 * through the screen and back down, which couples two sections that otherwise
 * do not know about each other, and leaves the hint wrong for as long as the
 * other section is still loading.
 *
 * Collapse the two the day the Security screen loads the passkey LIST for
 * itself and hands it down. That is a real refactor of a section that adds,
 * renames and deletes them, and it is not this change's to make.
 */
export async function loadPasskeyCount(): Promise<number> {
  try {
    const { data, error } = await authClient.passkey.listUserPasskeys();
    if (error || !Array.isArray(data)) return -1;
    return data.length;
  } catch {
    return -1;
  }
}

/**
 * Will the address being LEFT have to approve this change?
 *
 * ── ADVISORY. THE SERVER DECIDES, AND THIS ONLY WARNS ────────────────
 *
 * A copy of `needsCurrentAddressApproval` in the auth service, and a copy of a
 * server rule is a thing that drifts — which is why the pending card reads
 * `awaitingApproval` off the record instead of calling this. What this is for
 * is the sentence BEFORE the submit: Jon's point on account#54 is that the
 * account already knows it will be gated, so leaving somebody to discover it
 * from an email that never arrives at the inbox they were watching is a choice
 * rather than a limitation.
 *
 * Being wrong costs a misleading warning, not a wrong outcome. So it errs
 * toward silence: an unknown passkey count returns false, and the form says
 * nothing rather than promising an extra step that may not exist.
 *
 * ── THE RULE IS OWASP'S, AND WHAT LIFTS IT IS MAILBOX-INDEPENDENCE ───
 *
 * Their control for changing a registered address splits on how strongly
 * identity was proved at the moment of the change: with MFA the old address is
 * notified, with a password alone it must approve. The gate is a SUBSTITUTE for
 * strong proof.
 *
 * A passkey or TOTP lifts it, and the property they share is narrower than
 * "strong": neither can be obtained by taking over the mailbox.
 *
 * A LINKED PROVIDER USED TO LIFT IT AND NO LONGER DOES (Jon, 2026-09-02), so
 * do not restore it from the OWASP text alone. A work SSO identity is usually
 * derived from the very address being changed — read RidgeWill/auth#324's own
 * scenario one step further and whoever inherits joe@company.com also inherits
 * the Workspace identity behind it, sends exactly the request the legitimate
 * user would, and moves the account away. The auth service's
 * `needsCurrentAddressApproval` carries the full argument.
 */
export function willNeedCurrentAddressApproval(account: {
  hasPassword: boolean;
  twoFactorEnabled: boolean;
  /** Negative when the lookup failed. */
  passkeyCount: number;
  /** No longer consulted. Kept so the omission reads as a decision rather than
   *  as something forgotten, and so this shape still matches the server's. */
  providerCount?: number;
}): boolean {
  if (!account.hasPassword) return false;
  if (account.twoFactorEnabled) return false;
  if (account.passkeyCount !== 0) return false;
  return true;
}

/**
 * Check a password without doing anything with it. Null on success, or a
 * message to show.
 *
 * Never throws, for the reason `requestChange` does not: the caller is a submit
 * handler in a modal, and an unhandled rejection there leaves a dialog spinning
 * with no way out but Escape.
 *
 * ── THE REFUSALS ────────────────────────────────────────────────────
 *
 * `INVALID_PASSWORD` is the one people will hit, and the server already wrote
 * the sentence for it.
 *
 * `CREDENTIAL_ACCOUNT_NOT_FOUND` should be unreachable: the gate is only opened
 * for an account whose `hasPassword` is true. It is translated rather than
 * passed through for the reason `unlinkProvider` translates its own unreachable
 * case — "unreachable" depends on the client's answer agreeing with the
 * server's, and the day they disagree is the day somebody needs the message to
 * make sense.
 *
 * `429` is worth naming rather than showing raw. The endpoint carries the same
 * three-attempts-per-ten-seconds rule as `/sign-in`, because it is a password
 * oracle, and somebody mistyping twice in a hurry will meet it.
 */
export async function verifyPassword(password: string): Promise<string | null> {
  try {
    await reauthenticate(password);
    return null;
  } catch (e) {
    if (e instanceof ApiError) {
      if (e.status === 429) {
        return 'Too many attempts just now. Wait a few seconds and try again.';
      }
      /**
       * THE ROUTE IS NOT THERE, WHICH IS A DEPLOY SKEW RATHER THAN A REFUSAL.
       *
       * This app deploys independently of the auth service, and on 2026-09-01
       * it deployed FIRST: the portal was live with the security gate while
       * `/reauthenticate` was still 404 on auth-dev for several minutes, and
       * the fallback message read "POST /reauthenticate failed: 404".
       *
       * That is the account deployment's allow-list answering, or a service
       * from before RidgeWill/auth#339 — either way it is not something the
       * person can fix by trying again in a moment, so the copy does not
       * suggest it. `requestChange` says the same about `/change-email` for
       * the same reason.
       */
      if (e.status === 404) {
        return 'This site cannot confirm your password just now. Nothing has changed.';
      }
      if (e.code === 'CREDENTIAL_ACCOUNT_NOT_FOUND') {
        return 'This account does not have a password, so there is nothing to confirm.';
      }
      return e.message;
    }
    return 'That could not be checked just now. Nothing has changed.';
  }
}

/**
 * Do the two typed addresses agree?
 *
 * ── WHY THE FORM ASKS TWICE (Jon, 2026-09-02) ────────────────────────
 *
 * A mistyped address is not a refusal, it is a SILENT one. `/change-email`
 * answers 200 whether or not anything was sent — deliberately, so the form
 * cannot be used to find out who has an account here — so a typo produces the
 * same screen as a success, a pending card naming an address nobody reads, and
 * a wait for a link that never arrives.
 *
 * Every other field on this page can be corrected afterwards by looking at it.
 * This one cannot: the only place the mistake becomes visible is an inbox the
 * person does not own.
 *
 * ── COMPARED CASE-INSENSITIVELY, AND TRIMMED ─────────────────────────
 *
 * The server lower-cases before it stores, so `Jon@` and `jon@` are the same
 * address and refusing that pair would be this app inventing a rule the service
 * does not have. Trimmed for the same reason a paste from a mail client often
 * carries a trailing space.
 *
 * NOT normalised further. `jon+news@gmail.com` and `jon@gmail.com` ARE the same
 * account to the service's normalisation, and treating them as a match here
 * would accept two addresses that deliver to one inbox as a confirmation of
 * each other — which is exactly the class of mistake this is meant to catch.
 */
export function addressesAgree(first: string, second: string): boolean {
  return first.trim().toLowerCase() === second.trim().toLowerCase();
}
